import React, { useContext, useState } from "react";
import { UserContext } from "../../UserProvider";
import { State } from "./types";
import useStyles from "./styles";
import { hasRole } from "../../models/User";
import { permissions } from "../../constants";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";
import { getReportItems } from "./data";
import {
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField
} from "@material-ui/core";
import { DateTimePicker, KeyboardDatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { StoreSelect } from "../../components/selects/StoreSelect";
import { toInteger } from "lodash";
import { useI18n } from "../../hooks/useI18n";

export const Reports = () => {
  const classes = useStyles();
  const user = useContext(UserContext);
  const { t } = useI18n();
  const [state, setState] = useState<State>(
    user.type === "ANONYMOUS" || !hasRole(user, permissions.reports)
      ? {
          type: "NOT_ALLOWED"
        }
      : {
          type: "READY",
          items: getReportItems()
        }
  );
  const periodFromName = "periodFrom";
  const periodToName = "periodTo";

  switch (state.type) {
    case "UNEXPECTED_ERROR":
      return (
        <Alert severity="error">
          <AlertTitle>
            {state.problem.status}, {state.problem.title}
          </AlertTitle>
          {state.problem.detail} <strong>{state.problem.traceId}</strong>
        </Alert>
      );

    case "NOT_ALLOWED":
      return (
        <>
          <Alert severity="warning">{t("common.notAllowedToView")}</Alert>
        </>
      );

    case "READY":
      const onSelected = (path: string) => {
        let item = state.items.find(i => i.path === path);

        if (!item) {
          return;
        }

        return setState({ ...state, currentItem: item, values: {} });
      };

      const isValid = (): boolean => {
        if (!state.currentItem || !state.values) {
          return false;
        }

        let values = state.values;

        if (state.currentItem.parameters == undefined) {
          return true;
        }

        return state.currentItem.parameters
          .filter(p => p.required)
          .every(p => {
            switch (p.type) {
              case "dateTimeSpan":
              case "dateSpan":
                let periodFromValid = false;
                let periodToValid = false;

                if (values[periodFromName] && values[periodFromName].value) {
                  periodFromValid = true;
                } else if (!p.required) {
                  periodFromValid = true;
                }

                if (values[periodToName] && values[periodToName].value) {
                  periodToValid = true;
                } else if (!p.required) {
                  periodToValid = true;
                }

                return periodFromValid && periodToValid;

              case "boolean":
                if (
                  values[p.variableName] &&
                  values[p.variableName].value !== undefined
                ) {
                  return true;
                } else if (!p.required) {
                  return true;
                }
                return false;
            }
          });
      };

      const onDownload = (type: "excel" | "csv" | "pdf") => {
        if (!state.currentItem || !state.values) {
          return;
        }

        let url = `/v1/reports${state.currentItem.path}/${type}`;

        let query: Array<string> = [];

        for (let key in state.values) {
          if (!state.values.hasOwnProperty(key)) {
            continue;
          }

          let value = state.values[key];

          query.push(`${key}=${value.value}`);
        }

        url = `${url}?${query.join("&")}`;
        window.open(url);
      };

      return (
        <>
          <Card className={classes.section}>
            <FormControl variant={"standard"} className={classes.field}>
              <InputLabel id="report-select-label">
                {t("common.selectReportTypeToDownload")}
              </InputLabel>
              <Select
                id="report-select"
                labelId="report-select-label"
                value={state.currentItem?.path ?? ""}
                onChange={e => onSelected(e.target.value as string)}
                autoWidth={true}
              >
                {state.items
                  .filter(
                    i =>
                      i.permissions.length === 0 || hasRole(user, i.permissions)
                  )
                  .map(i => (
                    <MenuItem key={i.path} value={i.path}>
                      <ListItemText primary={i.name} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {state.currentItem ? (
              <>
                {state.currentItem.parameters?.map(p => {
                  switch (p.type) {
                    case "store":
                      return (
                        <StoreSelect
                          className={classes.field}
                          filterOpened={true}
                          onSelected={store => {
                            let values = state.values || {};

                            values[p.variableName] =
                              values[p.variableName] ?? {};

                            values[p.variableName].value = store.id;

                            setState({ ...state, values: values });
                          }}
                        />
                      );
                    case "number":
                      return (
                        <TextField
                          type="number"
                          className={classes.field}
                          label={p.displayName}
                          value={
                            state.values && state.values[p.variableName]
                              ? state.values[p.variableName].value
                              : ""
                          }
                          onChange={e => {
                            let values = state.values || {};

                            values[p.variableName] =
                              values[p.variableName] ?? {};

                            values[p.variableName].value = toInteger(
                              e.target.value
                            );

                            setState({ ...state, values: values });
                          }}
                        />
                      );
                    case "dateSpan":
                      return (
                        <>
                          <KeyboardDatePicker
                            disableToolbar
                            variant="inline"
                            format="YYYY-MM-DD"
                            margin="normal"
                            id="period-from-date"
                            label={t("common.formFields.fromDate")}
                            required={p.required}
                            value={
                              state.values && state.values[periodFromName]
                                ? state.values[periodFromName].value
                                : null
                            }
                            onChange={(date: MaterialUiPickersDate) => {
                              if (date) {
                                let values = state.values;
                                values = values ?? {};
                                values[periodFromName] =
                                  values[periodFromName] ?? {};
                                values[periodFromName].value =
                                  date.format("YYYY-MM-DD");
                                values[periodFromName].ref = p;
                                return setState({ ...state, values });
                              }
                            }}
                            KeyboardButtonProps={{
                              "aria-label": "change from date"
                            }}
                          />
                          <KeyboardDatePicker
                            disableToolbar
                            variant="inline"
                            format="YYYY-MM-DD"
                            margin="normal"
                            id="period-to-date"
                            label={t("common.formFields.toDate")}
                            required={p.required}
                            value={
                              state.values && state.values[periodToName]
                                ? state.values[periodToName].value
                                : null
                            }
                            onChange={(date: MaterialUiPickersDate) => {
                              if (date) {
                                let values = state.values;
                                values = values ?? {};
                                values[periodToName] =
                                  values[periodToName] ?? {};
                                values[periodToName].value =
                                  date.format("YYYY-MM-DD");
                                values[periodToName].ref = p;
                                return setState({ ...state, values });
                              }
                            }}
                            KeyboardButtonProps={{
                              "aria-label": "change to date"
                            }}
                          />
                        </>
                      );

                    case "dateTimeSpan":
                      return (
                        <>
                          <DateTimePicker
                            format="YYYY-MM-DD HH:mm"
                            label={t("common.from")}
                            ampm={false}
                            value={
                              state.values && state.values[periodFromName]
                                ? state.values[periodFromName].value
                                : null
                            }
                            onChange={newValue => {
                              let values = state.values;
                              values = values ?? {};
                              values[periodFromName] =
                                values[periodFromName] ?? {};

                              values[periodFromName].value =
                                newValue?.format("YYYY-MM-DD HH:mm");

                              setState({ ...state, values });
                            }}
                          />
                          <DateTimePicker
                            format="YYYY-MM-DD HH:mm"
                            label={t("common.to")}
                            ampm={false}
                            value={
                              state.values && state.values[periodToName]
                                ? state.values[periodToName].value
                                : null
                            }
                            onChange={newValue => {
                              let values = state.values;
                              values = values ?? {};
                              values[periodToName] = values[periodToName] ?? {};

                              values[periodToName].value =
                                newValue?.format("YYYY-MM-DD HH:mm");

                              setState({ ...state, values });
                            }}
                          />
                        </>
                      );

                    case "boolean":
                      return (
                        <div style={{ marginTop: "2rem" }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  state.values && state.values[p.variableName]
                                    ? state.values[p.variableName].value
                                    : p.defaultValue
                                }
                                onChange={e => {
                                  let values = state.values;
                                  values = values ?? {};
                                  values[p.variableName] =
                                    values[p.variableName] ?? {};
                                  values[p.variableName].value =
                                    e.target.checked;
                                  values[p.variableName].ref = p;
                                  return setState({ ...state, values });
                                }}
                              />
                            }
                            label={p.displayName}
                          />
                        </div>
                      );
                  }
                })}
                <div style={{ marginTop: "2rem" }}>
                  {state.currentItem.fileTypes.map(ft => (
                    <Button
                      key={`download-${ft}`}
                      variant={"contained"}
                      color={"primary"}
                      style={{ marginRight: "1rem" }}
                      disabled={!isValid()}
                      onClick={() => onDownload(ft)}
                    >
                      {t("common.download")} {ft.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </>
            ) : null}
          </Card>
        </>
      );
  }
};
